import axios from 'axios'
import { Message } from 'element-ui'
import store from '@/store'
import router from '@/router'
import i18n from '@/i18n'

const service = axios.create({
  baseURL: '/api/v1',
  timeout: 30000
})

// ===== Token 自动续期 =====
let isRefreshing = false
let refreshSubscribers = []

function onRefreshed(token) {
  refreshSubscribers.forEach(cb => cb(token))
  refreshSubscribers = []
}

function addRefreshSubscriber(cb) {
  refreshSubscribers.push(cb)
}

async function refreshToken() {
  if (isRefreshing) return new Promise(resolve => addRefreshSubscriber(resolve))
  isRefreshing = true
  try {
    const res = await axios.post('/api/v1/auth/refresh', null, {
      headers: { 'Authorization': 'Bearer ' + store.state.user.token }
    })
    const newToken = res.data?.data?.token
    if (newToken) {
      store.commit('user/SET_TOKEN', newToken)
      onRefreshed(newToken)
    }
    return newToken
  } catch (e) {
    store.dispatch('user/logout')
    router.push('/login')
    return null
  } finally {
    isRefreshing = false
  }
}

function getTokenRemainingTime() {
  const token = store.state.user.token
  if (!token) return 0
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return (payload.exp * 1000) - Date.now()
  } catch (e) {
    return 0
  }
}

// ===== 空闲检测 =====
const IDLE_TIMEOUT = 24 * 60 * 60 * 1000 // 24 小时无操作则退出
let lastActivityTime = Date.now()
let idleTimer = null

function resetIdleTimer() {
  lastActivityTime = Date.now()
}

function checkIdle() {
  if (Date.now() - lastActivityTime > IDLE_TIMEOUT) {
    if (store.state.user.token) {
      Message.warning(i18n.t('common.sessionExpired'))
      store.dispatch('user/logout')
      router.push('/login')
    }
    return
  }
  idleTimer = setTimeout(checkIdle, 60 * 1000) // 每分钟检查
}

function startIdleDetection() {
  const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart']
  events.forEach(e => window.addEventListener(e, resetIdleTimer, { passive: true }))
  resetIdleTimer()
  idleTimer = setTimeout(checkIdle, 60 * 1000)
}

function stopIdleDetection() {
  if (idleTimer) clearTimeout(idleTimer)
}

// 监听登录状态（延迟执行避免循环依赖）
let idleWatchInitialized = false
function initIdleWatch() {
  if (idleWatchInitialized) return
  idleWatchInitialized = true
  store.watch(
    state => state.user.token,
    token => {
      if (token) {
        startIdleDetection()
      } else {
        stopIdleDetection()
      }
    },
    { immediate: true }
  )
}
// 在下一个事件循环初始化
setTimeout(initIdleWatch, 0)

// ===== 请求拦截器 =====
service.interceptors.request.use(
  config => {
    const token = store.state.user.token
    if (token) config.headers['Authorization'] = 'Bearer ' + token
    return config
  },
  error => Promise.reject(error)
)

// ===== 响应拦截器 =====
service.interceptors.response.use(
  response => {
    // 文件流下载（Blob）：直接返回二进制数据，不按业务 JSON 的 code 判断
    if (response.config.responseType === 'blob') {
      return response.data
    }
    const res = response.data
    if (res.code !== 200) {
      Message.error(res.msg || 'Error')
      if (res.code === 401) {
        store.dispatch('user/logout')
        router.push('/login')
      }
      return Promise.reject(new Error(res.msg))
    }
    return res
  },
  async error => {
    const originalRequest = error.config
    const status = error.response?.status

    // 401 且非刷新请求：自动续期
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      const newToken = await refreshToken()
      if (newToken) {
        originalRequest.headers['Authorization'] = 'Bearer ' + newToken
        return service(originalRequest)
      }
    }

    // Token 即将过期：后台静默续期
    const remaining = getTokenRemainingTime()
    if (remaining > 0 && remaining < 5 * 60 * 1000) { // < 5 分钟
      refreshToken()
    }

    Message.error(error.message || 'Network Error')
    return Promise.reject(error)
  }
)

export default service
